// ============================================================
// THE WINGS CARD'S THUMBNAIL - a scene's set as a wireframe seen from above
// (plan map-display, I; the v48 study: "a wings card with a wireframe
// thumb").
//
// Each solid is its footprint: the bounding box of its geometry on the
// ground, fitted with the rest inside the card's box at one scale, so the
// map keeps its proportions. Outlines only - it is a reminder of which room
// this is, not a map to read.
//
// Pure. Zero React dependency.
// ============================================================
import type { Solid } from '../battlefield/types';
import { boundingBox, solidGeometry } from '../battlefield/solids';

export interface ThumbRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Space kept round the drawing, in thumbnail units. */
const MARGIN = 3;

/** A ground rectangle: x from x0 to x1, z from z0 to z1. */
interface Span {
  x0: number;
  z0: number;
  x1: number;
  z1: number;
}

/** What spans them all, in one pass (no argument spreads: a large map has many solids). */
function spanOf(spans: readonly Span[]): Span {
  const all = { x0: Infinity, z0: Infinity, x1: -Infinity, z1: -Infinity };
  for (const s of spans) {
    all.x0 = Math.min(all.x0, s.x0);
    all.z0 = Math.min(all.z0, s.z0);
    all.x1 = Math.max(all.x1, s.x1);
    all.z1 = Math.max(all.z1, s.z1);
  }
  return all;
}

/**
 * The solids' footprints, fitted into a `width` x `height` box at one scale; empty when nothing is built. Fitted
 * to `extent` - the map's own bounds - when given, so a lone crate is a crate in a room and not the whole card.
 */
export function thumbRects(
  solids: readonly Solid[],
  width: number,
  height: number,
  extent?: { min: { x: number; z: number }; max: { x: number; z: number } },
): ThumbRect[] {
  if (!solids.length) return [];
  const feet = solids.map((s) => {
    const b = boundingBox(solidGeometry(s));
    return { x0: b.min.x, z0: b.min.z, x1: b.max.x, z1: b.max.z };
  });
  const frame = extent
    ? spanOf([...feet, { x0: extent.min.x, z0: extent.min.z, x1: extent.max.x, z1: extent.max.z }])
    : spanOf(feet);
  const scale = Math.min(
    (width - 2 * MARGIN) / (frame.x1 - frame.x0 || 1),
    (height - 2 * MARGIN) / (frame.z1 - frame.z0 || 1),
  );
  return feet.map((f) => ({
    x: MARGIN + (f.x0 - frame.x0) * scale,
    y: MARGIN + (f.z0 - frame.z0) * scale,
    w: (f.x1 - f.x0) * scale,
    h: (f.z1 - f.z0) * scale,
  }));
}
